/**
 * DocumentListTable - Displays a table of documents with actions
 *
 * Shows all uploaded documents for a transaction with their status,
 * file details, and available actions (Process, Preview, Delete).
 */

import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, CheckCircle, Download, Eye, FileText, Loader2, Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDeleteDocument, useDocuments, useDownloadDocument } from '../../hooks';
import { DocumentStatusBadge } from './DocumentStatusBadge';

export interface DocumentListTableProps {
  /** Transaction ID to fetch documents for */
  transactionId: string;
  /** Callback when preview button is clicked */
  onPreview?: (documentId: string) => void;
}

// Document type matching the backend API response
interface ApiDocument {
  documentId: string;
  fileName: string;
  documentType: string;
  processingStatus: string;
  processing_status?: string; // API sends snake_case
  uploadedAt: string;
  fileSize: number;
  validationSessionId?: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Helper to get status from document (handles both camelCase and snake_case)
const getStatus = (doc: ApiDocument): string => {
  return doc.processingStatus || doc.processing_status || '';
};

/**
 * Safely format a date string to relative time
 * Handles invalid dates gracefully
 */
const formatRelativeTime = (dateString: string | Date): string => {
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    // Validate the date
    if (isNaN(date.getTime())) {
      return 'Unknown';
    }
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return 'Unknown';
  }
};

export function DocumentListTable({ transactionId, onPreview }: DocumentListTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<ApiDocument | null>(null);
  const navigate = useNavigate();

  // Note: useDocuments returns CDDocument[] but the API response has a different structure
  // We cast to ApiDocument which matches the actual API response
  const { data: documents = [], isLoading, error } = useDocuments(transactionId);
  const apiDocuments = (documents as unknown as ApiDocument[]) || [];
  const downloadMutation = useDownloadDocument();
  const deleteMutation = useDeleteDocument();

  const handleDownload = useCallback(
    async (document: ApiDocument) => {
      try {
        await downloadMutation.mutateAsync({
          transactionId,
          documentId: document.documentId,
        });
      } catch {
        // Error handled by mutation
      }
    },
    [downloadMutation, transactionId],
  );

  const handleDeleteClick = useCallback((document: ApiDocument) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!documentToDelete) return;

    try {
      await deleteMutation.mutateAsync({
        transactionId,
        documentId: documentToDelete.documentId,
      });

      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    } catch {
      // Error handled by mutation
    }
  }, [deleteMutation, documentToDelete, transactionId]);

  const handlePreview = useCallback(
    (document: ApiDocument) => {
      onPreview?.(document.documentId);
    },
    [onPreview],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <AlertCircle className="h-8 w-8 text-destructive mb-2" />
        <p className="text-sm text-muted-foreground">Failed to load documents</p>
      </div>
    );
  }

  if (apiDocuments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed rounded-lg">
        <FileText className="h-12 w-12 text-muted-foreground mb-3" />
        <p className="text-sm font-medium">No documents uploaded</p>
        <p className="text-xs text-muted-foreground mt-1">Upload a PDF document to get started</p>
      </div>
    );
  }

  const canValidate = (status: string) => status === 'validating';
  const canPreview = (status: string) => status === 'completed';

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>File Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {apiDocuments.map((doc: ApiDocument, index: number) => {
              // Use documentId as key, fall back to index if missing
              const rowKey = doc.documentId || `doc-${index}`;
              const status = getStatus(doc);

              return (
                <TableRow key={rowKey}>
                  <TableCell className="font-medium">{doc.fileName}</TableCell>
                  <TableCell>
                    <DocumentStatusBadge status={status} showIcon />
                  </TableCell>
                  <TableCell>{formatFileSize(doc.fileSize)}</TableCell>
                  <TableCell>{formatRelativeTime(doc.uploadedAt)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {/* {canProcess(status) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleProcess(doc)}
                          disabled={processMutation.isPending}
                          title="Process document"
                        >
                          {processMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                        </Button>
                      )} */}
                      {canValidate(status) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            navigate(`/cd/${transactionId}/documents/${doc.documentId}/validate`)
                          }
                          title="Validate document"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      )}
                      {canPreview(status) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handlePreview(doc)}
                          title="Preview extracted data"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDownload(doc)}
                        disabled={downloadMutation.isPending}
                        title="Download document"
                      >
                        {downloadMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteClick(doc)}
                        disabled={deleteMutation.isPending}
                        title="Delete document"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{documentToDelete?.fileName}&quot;? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
